import React, { useContext } from 'react';
import { useLoader } from '../api/useLoader';
import { PraksisstedApiContext } from '../api/PraksisstedApiContext';
import { useParams } from 'react-router-dom';
import { LoginApiContext } from '../api/LoginApiContext';
import Spinner from '../components/spinner/spinner';
import { useIntl } from 'react-intl';
import translations from './userprofile.i18n';
import { HttpError } from '../api/generated/studiested-generated-api';
import PageLayout from '../pagelayout';
import { BackButton } from '../pagelayout/backButton';
import PraksisstedUserprofile from './praksisstedUserprofile';
import Tilganger from './praksisstedbrukerTilganger';
import { PsaUserContext } from '../user/PsaUserStore';
import { BrukerrollerDto } from '../api/generated/praksissted-generated-api';
import {
  AlpNotificationType,
  NotificationFunctionContext,
} from '../components/notifications/notificationProvider';
import { uuidv4 } from '../util/uuidv4';
import Activities from './activities.psa';

const PraksisstedbrukerprofilPage: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const { psaUser } = useContext(PsaUserContext);
  const adminUser =
    psaUser && psaUser.hoeyesteRettighet === BrukerrollerDto.Administrator;

  const { id } = useParams<{ id: string }>();
  const { loginApi } = useContext(LoginApiContext);
  const { praksisstedApi } = useContext(PraksisstedApiContext);
  const { addNotification } = useContext(NotificationFunctionContext);

  const { data: userrole } = useLoader(() => loginApi.userroleGet(), []);
  const praksisstedNavn = (userrole && userrole.selectedRole!!.name) || '';
  const isSelf = userrole && userrole.selectedRole!.id === id;
  const canAdministerPermissions = (userrole && !isSelf && adminUser) || false;

  const { data: brukerprofil, reload: reloadBrukerprofil } = useLoader(
    () =>
      praksisstedApi.praksisstedbrukerprofilIdGet({
        pathParams: { id: id as string },
      }),
    [id],
    true,
    true,
  );

  const psBrukerProfile = React.useMemo(() => {
    return brukerprofil ? { ...brukerprofil, praksisstedNavn } : undefined;
  }, [brukerprofil, praksisstedNavn]);

  const { formatMessage } = useIntl();

  const labels = {
    pageTitle: formatMessage({ id: translations.pageTitlePSUser.id }),
    title: formatMessage(
      { id: translations.titlePraksissted.id },
      {
        institusjon: praksisstedNavn,
      },
    ),
    subtitle: formatMessage({ id: translations.subtitle.id }),
    tilbake: formatMessage({ id: translations.toPracticeplaces.id }),
    lagret: formatMessage({ id: translations.lagret.id }),
  };

  const savePraksisstedbrukerprofil = (
    fornavn: string,
    etternavn: string,
    epost: string,
    telefonnummer: string,
    omMeg: string,
  ) => {
    praksisstedApi
      .praksisstedbrukerprofilIdPut({
        pathParams: { id: id as string },
        praksisstedbrukerprofilPutDto: {
          fornavn,
          etternavn,
          epost,
          telefonnummer,
          omMeg,
        },
      })
      .then(() => {
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.SUCCESS,
          message: formatMessage({ id: translations.lagret.id }),
        });
        reloadBrukerprofil();
      })
      .catch((err: HttpError) =>
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.ERROR,
          message: err.body,
        }),
      );
  };

  if (!brukerprofil || !id) {
    return <Spinner />;
  }

  const { fornavn: fnavn, etternavn: enavn } = brukerprofil.bruker;
  const userName = `${fnavn} ${enavn}`;

  return (
    <PageLayout
      isIndented
      title={labels.title}
      subtitle={labels.subtitle}
      actionItems={
        <BackButton title={labels.tilbake} path={'/myorganization'} />
      }
    >
      <PraksisstedUserprofile
        brukerprofil={psBrukerProfile!}
        saveHandler={savePraksisstedbrukerprofil}
        editableFields={[
          'fornavn',
          'etternavn',
          'epost',
          'telefonnummer',
          'omMeg',
        ]}
      />
      <Tilganger
        brukerprofil={psBrukerProfile!}
        canAdminister={canAdministerPermissions}
        reloadBrukerprofil={reloadBrukerprofil}
        isSelf={isSelf}
      />
      <Activities
        userProfileId={id}
        userName={userName}
        isSelf={isSelf!!}
        isPraksisstedBruker
      />
    </PageLayout>
  );
};

export default PraksisstedbrukerprofilPage;
